import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './AppIcon';
import chatbotService from '../services/chatbotService';

const AIFAQChatbot = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (error) {
      console.error('Erreur de connexion chatbot:', error);
    }
  }, [error]);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!input?.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    const newMessages = [...messages, userMessage];
    setMessages([...newMessages, { role: 'assistant', content: '...' }]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      const reply = await chatbotService?.sendMessage(newMessages);
      setMessages([...newMessages, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError(err);
      setMessages([...newMessages, { role: 'assistant', content: err?.message || 'Désolée, je rencontre un problème technique. Veuillez réessayer. 😔' }]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickQuestions = [
    { icon: 'MapPin', text: 'Où déposer ma barrique ?' },
    { icon: 'Clock', text: 'Quel est le délai de livraison ?' },
    { icon: 'Search', text: 'Comment suivre mon colis ?' },
    { icon: 'DollarSign', text: 'Quels sont les tarifs ?' }
  ];

  const handleQuickQuestion = (question) => {
    setInput(question);
  };

  return (
    <div className="relative">
      {/* Toggle Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 bg-green-600 hover:bg-green-700 text-white rounded-full p-4 shadow-2xl flex items-center gap-2 transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {isOpen ? (
          <Icon name="X" size={24} />
        ) : (
          <>
            <Icon name="MessageCircle" size={24} />
            <span className="font-semibold hidden sm:inline">Besoin d'aide ?</span>
          </>
        )}
      </motion.button>
      {/* Chatbot Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-40 w-[90vw] sm:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                  <Icon name="Bot" size={24} className="text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Fatou - Assistant ADT</h3>
                  <p className="text-xs text-green-100">Réponse instantanée</p>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="h-96 overflow-y-auto p-4 bg-gray-50 space-y-4">
              {messages?.length === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="MessageCircle" size={32} className="text-green-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-2">Bonjour ! 👋</h4>
                  <p className="text-sm text-gray-600 mb-4">Je suis Fatou, votre assistante virtuelle ADT Express.</p>
                  <p className="text-xs text-gray-500 mb-4">Posez-moi vos questions sur :</p>
                  <div className="grid grid-cols-2 gap-2">
                    {quickQuestions?.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => handleQuickQuestion(q?.text)}
                        className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-200 hover:border-green-500 hover:bg-green-50 transition-colors text-left text-xs"
                      >
                        <Icon name={q?.icon} size={16} className="text-green-600 flex-shrink-0" />
                        <span className="text-gray-700">{q?.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages?.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className={`flex ${msg?.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                      msg?.role === 'user' ?'bg-green-600 text-white' :'bg-white text-gray-800 border border-gray-200'
                    }`}
                  >
                    {msg?.content === '...' ? (
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    ) : (
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg?.content}</p>
                    )}
                  </div>
                </motion.div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form onSubmit={handleSubmit} className="p-4 bg-white border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e?.target?.value)}
                  placeholder="Posez votre question..."
                  disabled={isLoading}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 text-sm"
                />
                <button
                  type="submit"
                  disabled={isLoading || !input?.trim()}
                  className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-4 py-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon name="Send" size={20} />
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AIFAQChatbot;